//================================================================
//  Page: Requests
//================================================================

//  Purpose: This page shows all requests at each stage

//  Example:
//    <Requests></Requests>    

//================================================================


// Libraries
import React, { useReducer, useEffect, useContext } from 'react';

// Contexts
import { SetToast } from '../../Library/GlobalContexts';

// Components
import PageComponent from '../../Components/PageComponent/PageComponent';
import TabView from '../../Components/TabView/TabView';

// Tabs
import Triage from './Tabs/Triage';
import ReviewAndNegotiate from './Tabs/ReviewAndNegotiate';
import Execute from './Tabs/Execute';
import Completed from './Tabs/Completed';
import Cancelled from './Tabs/Cancelled';
import All from './Tabs/All';
import RaisePO from './Tabs/RaisePO';

// Functions
import QueryListener from '../../Library/QueryListener';

// Images

// CSS
import './Requests.css';

export default function Requests() {

  //------------------------------------------------------
  //  useContext
  //------------------------------------------------------
  
  const setToast = useContext(SetToast);

  //------------------------------------------------------
  //  useReducer
  //------------------------------------------------------

  // Used to store the total requests per stage/view
  const [requests, setRequests] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      'triageRequests': [],
      'triageRequestsFiltered': [],
      'reviewAndNegotiateRequests': [],
      'reviewAndNegotiateRequestsFiltered': [],
      'raisePORequests': [],
      'raisePORequestsFiltered': [],
      'executeRequests': [],
      'executeRequestsFiltered': [],
    }
  );

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get all commercial requests in each stage
  useEffect(() => {

    const stages = [
      { key: 'triageRequests', stage: 'Triage', status: 'Not Started', additionalFilter: ['assignedto.email', '==', 'Unassigned'] },
      { key: 'reviewAndNegotiateRequests', stage: 'Review and Negotiate', status: 'In Progress' },
      { key: 'raisePORequests', stage: 'Raise PO', status: 'In Progress' },
      { key: 'executeRequests', stage: 'Execute', status: 'In Progress' },
    ];

    function onLoadChange(documents) {

      // Filter requests
      const requests = {};

      stages.forEach(stage => {
        requests[stage.key] = documents.filter(document => {
          if (stage.additionalFilter) {
            return document.stage === stage.stage && document.status === stage.status && document[stage.additionalFilter[0]] === stage.additionalFilter[1];
          } else {
            return document.stage === stage.stage && document.status === stage.status;
          }
        });
      });

      setRequests(requests);

    }

    function onError(error) {

      // Set error on toast
      setToast({
        'type': 'error',
        'message': 'Unable to load requests',
      });

      console.log(error);
    }

    const unsubscribe = QueryListener('requests', [
      ['assignmentgroup', '==', 'Commercial'],
    ], onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [])

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      body={
        <div>

          <TabView
            defaultView={1}

            //------------------------------------------------------
            //  Triage
            //------------------------------------------------------

            oneTitle={
              <div className='flex flex-row gap-2'>
                Triage
                <div className='flex items-center justify-center h-[22px] w-[22px] rounded-full bg-[#006866] text-white text-[12px] font-bold' hidden={requests?.triageRequests?.length === 0}>
                  {requests?.triageRequests?.length}
                </div>
              </div>
            }
            oneRoute={'/requests?view=triage'}
            oneContent={
              <Triage
                requests={requests}
                setRequests={setRequests}
              ></Triage>
            }

            //------------------------------------------------------
            //  Review and Negotiate
            //------------------------------------------------------

            twoTitle={
              <div className='flex flex-row gap-2'>
                Review and Negotiate
                <div className='flex items-center justify-center h-[22px] w-[22px] rounded-full bg-[#006866] text-white text-[12px] font-bold' hidden={requests?.reviewAndNegotiateRequests?.length === 0}>
                  {requests?.reviewAndNegotiateRequests?.length}
                </div>
              </div>
            }
            twoRoute={'/requests?view=reviewandnegotiate'}
            twoContent={
              <ReviewAndNegotiate
                requests={requests}
                setRequests={setRequests}
              ></ReviewAndNegotiate>
            }

            //------------------------------------------------------
            // Execute
            //------------------------------------------------------

            threeTitle={
              <div className='flex flex-row gap-2'>
                Execute
                <div className='flex items-center justify-center h-[22px] w-[22px] rounded-full bg-[#006866] text-white text-[12px] font-bold' hidden={requests?.executeRequests?.length === 0}>
                  {requests?.executeRequests?.length}
                </div>
              </div>
            }
            threeRoute={'/requests?view=execute'}
            threeContent={
              <Execute
                requests={requests}
                setRequests={setRequests}
              ></Execute>
            }

            //------------------------------------------------------
            // Raise PO
            //------------------------------------------------------

            fourTitle={
              <div className='flex flex-row gap-2'>
                Raise PO
                <div className='flex items-center justify-center h-[22px] w-[22px] rounded-full bg-[#006866] text-white text-[12px] font-bold' hidden={requests?.raisePORequests?.length === 0}>
                  {requests?.raisePORequests?.length}
                </div>
              </div>
            }
            fourRoute={'/requests?view=raisepo'}
            fourContent={
              <RaisePO
                requests={requests}
                setRequests={setRequests}
              ></RaisePO>
            }


            //------------------------------------------------------
            //  Completed
            //------------------------------------------------------

            fiveTitle={'Completed'}
            fiveRoute={'/requests?view=completed'}
            fiveContent={
              <Completed></Completed>
            }

            //------------------------------------------------------
            // Cancelled
            //------------------------------------------------------

            sixTitle={'Cancelled'}
            sixRoute={'/requests?view=cancelled'}
            sixContent={
              <Cancelled></Cancelled>
            }

            //------------------------------------------------------
            // All
            //------------------------------------------------------

            sevenTitle={'All'}
            sevenRoute={'/requests?view=all'}
            sevenContent={
              <All></All>
            }

          ></TabView>

        </div>
      }
    ></PageComponent>
  )
}
